import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Badge } from 'flowbite-react';
import { Formik, Form, Field } from 'formik';
import {
    Button, Icon, ClientSelectField, ChangeListItem, ChangePicker,
    DatepickerField, SelectField, ToggleField, Confirm,
} from '..';
import { Change, Track } from '../../models';
import { RootState, useAppDispatch, useAppSelector, updateTrack, deleteTrack } from '../../redux';
import type { Moment } from 'moment';

export type TrackEditModalProps = {
    show: boolean;
    track: Track;
    onClose: () => void;
    /** View-only: no save/delete, fields disabled (other users' tracks). */
    readOnly?: boolean;
};

/** Keep header/footer visible on short viewports; scroll only the body. */
const modalTheme = {
    root: {
        base: 'fixed top-0 right-0 left-0 z-50 h-modal h-screen overflow-x-hidden overflow-y-hidden md:inset-0 md:h-full',
    },
    content: {
        // Default theme uses h-full below md, which forces the overlay to scroll.
        base: 'relative w-full p-4 h-auto',
        inner: 'relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col min-h-0 h-auto max-h-[90vh] w-full overflow-hidden',
    },
    header: {
        base: 'flex shrink-0 items-start justify-between rounded-t border-b p-5 dark:border-gray-600',
    },
    body: {
        // Keep default p-6 — replacing base without it removes all inner padding.
        base: 'p-6 flex-1 overflow-y-auto min-h-0',
        popup: 'pt-0',
    },
    footer: {
        base: 'flex shrink-0 items-center space-x-2 rounded-b border-gray-200 p-6 dark:border-gray-600',
    },
};

function TrackEditModal(props: TrackEditModalProps) {
    const { show, track, onClose, readOnly = false } = props;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [changeFilter, setChangeFilter] = useState('');
    const [mileageOpen, setMileageOpen]   = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const { updating, deleting } = useAppSelector((state: RootState) => state.tracks);
    const { setting }  = useAppSelector((state: RootState) => state.settings);
    const mileageEnabled = !!setting?.mileage?.enabled;

    const hasLinkedChange = !!track.change?._id;

    const initialValues = useMemo(() => ({
        ...track,
        billingMode: (!hasLinkedChange && track.billingMode === 'floor')
            ? 'hourly'
            : (track.billingMode || 'hourly'),
        billingDev: !!track.billingDev,
        startAtTime: track.startAt.format('HH:mm'),
        stopAtTime: track.stopAt?.format('HH:mm'),
        startAtDay: track.startAt.clone().startOf('day'),
        stopAtDay: track.stopAt ? track.stopAt.clone().endOf('day') : undefined,
    }), [track, hasLinkedChange]);

    const billingModeOptionsFor = (linked: boolean) => [
        { value: 'hourly', label: t('tracks.billing_mode_hourly') },
        { value: 'package', label: t('tracks.billing_mode_package') },
        { value: 'free', label: t('tracks.billing_mode_free') },
        ...(linked
            ? [{ value: 'floor', label: t('tracks.billing_mode_floor') }]
            : []),
    ];

    const submit = (values: AnyObj, closeAfter = false) => {
        const { startAtDay, stopAtDay, startAtTime, stopAtTime, ...properties } = values;
        const [startHours, startMinutes] = startAtTime.split(':');

        properties.startAt
            .year(startAtDay.year())
            .month(startAtDay.month())
            .date(startAtDay.date());

        if (stopAtDay)
            properties.stopAt
                .year(stopAtDay.year())
                .month(stopAtDay.month())
                .date(stopAtDay.date());

        properties.startAt = properties.startAt
            .hours(parseInt(startHours, 10))
            .minutes(parseInt(startMinutes, 10))
            .seconds(0)
            .milliseconds(0);

        if (stopAtTime) {
            const [stopHours, stopMinutes] = stopAtTime.split(':');
            properties.stopAt = properties.stopAt
                .hours(parseInt(stopHours, 10))
                .minutes(parseInt(stopMinutes, 10))
                .seconds(0)
                .milliseconds(0);
        }

        const hasChange = !!(properties.change?._id
            || (typeof properties.change === 'string' && properties.change));
        properties.billingMode = properties.billingMode || 'hourly';
        if (properties.billingMode === 'floor' && !hasChange)
            properties.billingMode = 'hourly';
        properties.billingDev = properties.billingMode === 'free'
            ? false
            : !!properties.billingDev;
        delete properties.billable;

        if (mileageEnabled) {
            if (properties.km === '' || properties.km == null)
                delete properties.km;
            else {
                const km = Number(properties.km);
                if (!Number.isFinite(km) || km < 0)
                    delete properties.km;
                else
                    properties.km = km;
            }
        } else {
            delete properties.km;
        }

        dispatch(updateTrack(properties, false, false, (err: Error | null) => {
            if (!err && closeAfter)
                onClose();
        }));
    };

    const remove = () => {
        dispatch(deleteTrack(track._id, (err: Error | null) => {
            setConfirmDelete(false);
            if (!err)
                onClose();
        }));
    };

    return (
        <>
        <Modal show={show} size="2xl" dismissible onClose={onClose} theme={modalTheme}>
            <Modal.Header>
                <div className="flex flex-col gap-y-1 min-w-0">
                    <div className="flex items-center gap-x-2 min-w-0">
                        <Icon type="timer" size={5} className="shrink-0" />
                        <span className="truncate">
                            {readOnly ? t('tracks.view_title') : t('tracks.edit_title')}
                        </span>
                        {!track.isRunning() && (
                            <span className="shrink-0 text-sm font-bold text-gray-500 dark:text-gray-400">
                                {track.formattedDuration()}
                            </span>
                        )}
                    </div>
                    {track.author?.fullname && (
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 pl-7">
                            {t('tracks.author', { name: track.author.fullname() })}
                        </span>
                    )}
                </div>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                enableReinitialize
                onSubmit={(values) => {
                    if (readOnly)
                        return;
                    submit(values, true);
                }}
            >
                {({ values, dirty, setFieldValue, isSubmitting }) => (
                    <Form className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <Modal.Body>
                            <div className={`flex flex-col gap-y-5 ${readOnly ? 'pointer-events-none' : ''}`}>
                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.title')}
                                    </label>
                                    <Field
                                        type="text"
                                        name="title"
                                        required
                                        disabled={readOnly}
                                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-indigo-600 sm:text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:opacity-80"
                                    />
                                </div>

                                <div className="flex flex-col gap-y-1">
                                    <label
                                        htmlFor={`track-modal-description-${track._id}`}
                                        className="text-sm font-bold text-gray-700 dark:text-gray-200"
                                    >
                                        {t('tracks.description')}
                                    </label>
                                    <Field
                                        as="textarea"
                                        id={`track-modal-description-${track._id}`}
                                        name="description"
                                        rows={4}
                                        disabled={readOnly}
                                        placeholder={readOnly ? '' : (t('tracks.description_placeholder') || '')}
                                        className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm disabled:bg-gray-50 dark:disabled:bg-gray-800/50 disabled:opacity-80"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col gap-y-1">
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                            {t('tracks.project')}
                                        </span>
                                        <div className="rounded-md border border-gray-200 dark:border-gray-600 p-2">
                                            <Badge color="success">
                                                <div className="flex flex-row space-x-1.5 items-center">
                                                    <Icon type="project" size={3} />
                                                    <div className="text-xs font-bold truncate">
                                                        {track.project?.name}
                                                    </div>
                                                </div>
                                            </Badge>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-y-1">
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                            {t('tracks.client')}
                                        </span>
                                        {readOnly ? (
                                            <div className="rounded-md border border-gray-200 dark:border-gray-600 p-2">
                                                <Badge color="warning">
                                                    <div className="flex flex-row space-x-1.5 items-center">
                                                        <Icon type="user" size={3} />
                                                        <div className="text-xs font-bold truncate">
                                                            {track.client?.name?.() || '—'}
                                                        </div>
                                                    </div>
                                                </Badge>
                                            </div>
                                        ) : (
                                            <ClientSelectField name="client" />
                                        )}
                                    </div>
                                </div>

                                {track.project?._id && (
                                    <div className="flex flex-col gap-y-2">
                                        <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                            {t('tracks.change')}
                                        </span>
                                        {values.change?._id ? (
                                            <ChangeListItem
                                                change={values.change}
                                                onRemove={readOnly ? undefined : () => {
                                                    setFieldValue('change', undefined);
                                                    if (values.billingMode === 'floor')
                                                        setFieldValue('billingMode', 'hourly');
                                                }}
                                            />
                                        ) : readOnly ? (
                                            <span className="text-sm text-gray-400 italic">—</span>
                                        ) : (
                                            <>
                                                <div className="relative">
                                                    <input
                                                        type="text"
                                                        value={changeFilter}
                                                        placeholder={t('tracks.search_change') || ''}
                                                        className="block w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                                        onChange={(e: AnyObj) => setChangeFilter(e.target.value)}
                                                    />
                                                    <Icon type="search" className="absolute top-2 right-2" size={5} color="gray-300" />
                                                </div>
                                                <ChangePicker
                                                    acceptUnestimated
                                                    acceptFinished
                                                    hideLoader
                                                    defaultProjectId={track.project._id}
                                                    changeClassName="rounded-l-none"
                                                    onSelect={(change: Change) => {
                                                        if (change)
                                                            setFieldValue('change', change);
                                                        else
                                                            setFieldValue('change', undefined);
                                                        setChangeFilter('');
                                                    }}
                                                    filterChanges={(changes: Change[]) => {
                                                        if (!changeFilter?.length)
                                                            return [];
                                                        return changes
                                                            .filter((change: Change) => change.match(changeFilter))
                                                            .sort((a: Change, b: Change) => b.slug - a.slug);
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="flex flex-col gap-y-2">
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {t('tracks.billing')}
                                    </span>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-gray-200 dark:border-gray-600 p-3">
                                        {readOnly ? (
                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                {billingModeOptionsFor(!!values.change?._id)
                                                    .find(o => o.value === values.billingMode)?.label
                                                    || values.billingMode}
                                                {values.billingMode !== 'free' && values.billingDev
                                                    ? ` · ${t('tracks.billing_dev')}`
                                                    : ''}
                                            </div>
                                        ) : (
                                        <>
                                        <div className="flex-1 min-w-[12rem]">
                                            <SelectField
                                                name="billingMode"
                                                options={billingModeOptionsFor(!!values.change?._id)}
                                                onChange={(mode: string) => {
                                                    if (mode === 'free')
                                                        setFieldValue('billingDev', false);
                                                }}
                                            />
                                        </div>
                                        {values.billingMode !== 'free' && (
                                        <div className="flex items-center justify-between gap-x-3 shrink-0">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                {t('tracks.billing_dev')}
                                            </span>
                                            <ToggleField name="billingDev" />
                                        </div>
                                        )}
                                        </>
                                        )}
                                    </div>
                                </div>

                                <div className={`grid gap-4 ${track.stopAt ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                                    <div className="flex flex-col gap-y-2 items-center rounded-md border border-gray-200 dark:border-gray-600 p-3">
                                        <span className="text-sm font-bold">{t('tracks.start')}</span>
                                        {readOnly ? (
                                            <>
                                                <span className="text-sm font-medium">{track.startAt.format('LL')}</span>
                                                <span className="font-bold text-sm">{track.startAt.format('HH:mm')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <DatepickerField
                                                    name="startAtDay"
                                                    onSave={(_field: string, value: Moment) => {
                                                        if (value)
                                                            setFieldValue('startAtDay', value);
                                                    }}
                                                    inputClassName="bg-transparent border-0 text-center font-medium"
                                                    unclearable
                                                    openOnFocus={false}
                                                />
                                                <Field
                                                    type="time"
                                                    name="startAtTime"
                                                    required
                                                    className="block focus:ring-0 border-0 border-b-2 border-gray-300 dark:border-gray-500 py-1.5 shadow-sm font-bold text-sm w-fit bg-transparent"
                                                />
                                            </>
                                        )}
                                    </div>
                                    {track.stopAt && (
                                        <div className="flex flex-col gap-y-2 items-center rounded-md border border-gray-200 dark:border-gray-600 p-3">
                                            <span className="text-sm font-bold">{t('tracks.end')}</span>
                                            {readOnly ? (
                                                <>
                                                    <span className="text-sm font-medium">{track.stopAt.format('LL')}</span>
                                                    <span className="font-bold text-sm">{track.stopAt.format('HH:mm')}</span>
                                                </>
                                            ) : (
                                                <>
                                                    <DatepickerField
                                                        name="stopAtDay"
                                                        onSave={(_field: string, value: Moment) => {
                                                            setFieldValue('stopAtDay', value);
                                                        }}
                                                        inputClassName="bg-transparent border-0 text-center font-medium"
                                                        unclearable
                                                        openOnFocus={false}
                                                    />
                                                    <Field
                                                        type="time"
                                                        name="stopAtTime"
                                                        required
                                                        className="block focus:ring-0 border-0 border-b-2 border-gray-300 dark:border-gray-500 py-1.5 shadow-sm font-bold text-sm w-fit bg-transparent"
                                                    />
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {mileageEnabled && (readOnly
                                    ? (values.km != null && Number(values.km) > 0 && (
                                        <div className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5">
                                            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                                {t('tracks.mileage_section')}
                                            </span>
                                            <div className="text-sm text-gray-700 dark:text-gray-200 mt-1">
                                                {values.km} {t('tracks.km_unit')}
                                            </div>
                                        </div>
                                    ))
                                    : (
                                    <div className="rounded-lg border border-gray-300 dark:border-gray-600">
                                        <button
                                            type="button"
                                            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none"
                                            onClick={() => setMileageOpen(!mileageOpen)}
                                            aria-expanded={mileageOpen}
                                        >
                                            <Icon
                                                type={mileageOpen ? 'up' : 'down'}
                                                size={5}
                                                className="shrink-0"
                                            />
                                            <div className="flex min-w-0 flex-1 flex-col">
                                                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                                    {t('tracks.mileage_section')}
                                                </span>
                                                {!mileageOpen && (
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {values.km != null && Number(values.km) > 0
                                                            ? t('tracks.mileage_section_summary', { km: values.km })
                                                            : t('tracks.mileage_section_hint')}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        {mileageOpen && (
                                            <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-3">
                                                <label
                                                    htmlFor={`track-modal-km-${track._id}`}
                                                    className="text-sm font-medium text-gray-900 dark:text-gray-100"
                                                >
                                                    {t('tracks.km')}
                                                </label>
                                                <Field
                                                    id={`track-modal-km-${track._id}`}
                                                    type="number"
                                                    name="km"
                                                    min="0"
                                                    step="0.1"
                                                    className="mt-1 block w-28 rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 sm:text-sm"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    )
                                )}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            {readOnly ? (
                                <div className="flex w-full justify-end">
                                    <Button
                                        type="button"
                                        color="default"
                                        title={t('common.close')}
                                        onClick={onClose}
                                    />
                                </div>
                            ) : (
                            <div className="flex w-full items-center justify-between gap-3">
                                <Button
                                    type="button"
                                    color="danger"
                                    title={t('common.delete')}
                                    icon="delete"
                                    loading={deleting === 'pending'}
                                    onClick={() => setConfirmDelete(true)}
                                />
                                <div className="flex gap-3">
                                    <Button
                                        type="button"
                                        color="default"
                                        title={t('common.cancel')}
                                        onClick={onClose}
                                    />
                                    <Button
                                        type="submit"
                                        color="primary"
                                        title={t('common.save')}
                                        icon="check"
                                        loading={updating === 'pending' || isSubmitting}
                                        disabled={!dirty && updating !== 'pending'}
                                    />
                                </div>
                            </div>
                            )}
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
        <Confirm
            visible={!readOnly && confirmDelete}
            iconType="delete"
            title={t('tracks.confirm_delete') || undefined}
            confirmText={t('common.delete') || undefined}
            onConfirm={remove}
            onCancel={() => setConfirmDelete(false)}
        />
        </>
    );
}

export default TrackEditModal;
